import React, {
  useEffect,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import strings from "translate/language";
import {
  handleListResponse,
  ValidateExtensionImge,
  hasFloorValue,
  normalizeFloorValue,
} from "utils/utils";
import {
  filterEligibleBookingInventoryFlats,
  getEligibleFloorsForConfiguration,
  getInventoryFlatDisplayParts,
  getUniqueFlatTypesFromInventory,
} from "utils/inventoryAvailability";
import { getAppointmentDetail } from "../../reduxStore/action/appointmentActions";
import { removeAddBookingData } from "../../reduxStore/action/bookingActions";
import { ADD_BOOKING } from "../../reduxStore/types";
import bookingService from "../../reduxStore/service/bookingService";
import {
  getSiteVisitStatusData,
  updateSiteVisitStatus,
} from "../../reduxStore/action/siteVisitActions";
import { visitorDetail } from "reduxStore/action/visitorActions";
import { paymentTypes } from "reduxStore/action/masterActions";
import Breadcrumb from "component/common/breadcrumb";
import { handleDetailResponse, isEmpty } from "../../utils/utils";
import { apiCall, errorToast, unAuthorizedError } from "utils/httpClient";
import apiEndPoints from "utils/apiEndPoints";
import { toast } from "react-toastify";
import { showActionSuccessWithMdoc } from "utils/mdocSyncToasts";
import { getPropertyDetail } from "reduxStore/action/propertyActions";
import "./BookingListing.css";

/** Normalize JW / proxy inventory payloads into a flat list. */
function extractInventoryRows(apiResponse) {
  const body = apiResponse?.data;
  const jwPayload = body?.data ?? body;
  const candidates = [
    jwPayload?.result?.data,
    jwPayload?.data?.result?.data,
    jwPayload?.data,
    jwPayload?.result,
    Array.isArray(jwPayload) ? jwPayload : null,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
}

function inventoryErrorMessage(apiResponse) {
  const body = apiResponse?.data;
  const resultData = body?.result?.data ?? body?.data;
  if (typeof resultData === "string" && resultData.trim()) {
    const trimmed = resultData.trim();
    if (/access\s*denied/i.test(trimmed)) {
      return strings.jwInventoryAccessDenied;
    }
    return trimmed;
  }
  const message = typeof body?.message === "string" ? body.message.trim() : "";
  if (message && message !== "No data found") {
    if (/access\s*denied/i.test(message)) {
      return strings.jwInventoryAccessDenied;
    }
    if (apiResponse?.status >= 400 || body?.status >= 400) {
      return message;
    }
  }
  return strings.jwInventoryLoadFailed || strings.noInventoryLoaded;
}

const createEmptyFlatBooking = () => ({
  amount: "",
  payment_type: "",
  agreement_value: "",
  rate_achieved: "",
  configurations: "",
  floors: "",
  floor: "",
  flat_name: "",
  saleable_area: "",
  carpet_area: "",
  comment: "",
  chequePreview: "",
  chequeRaw: null,
});

const AddBooking = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const [formErrors, setFormErrors] = useState([{}]);
  const [formVaild, setFormVaild] = useState(null);
  const [appointmentData, setAppointmentData] = useState({});
  const [flatBookings, setFlatBookings] = useState([createEmptyFlatBooking()]);
  const [visitor, setVisitor] = useState({});
  const [loading, setLoading] = useState(false);
  const [flatTypes, setFlatTypes] = useState([]);
  const [masterType, setMasterType] = useState([]);
  const [mainInventory, setMainInventory] = useState([]);
  const [okBookingIsVisible, setOkBookingIsVisible] = useState(false);
  const [propDetails, setPropDetails] = useState({});
  const [siteVisitMapping, setSiteVisitMapping] = useState(null);
  const [inventoryLoadState, setInventoryLoadState] = useState("idle");
  const [inventoryHint, setInventoryHint] = useState("");
  const [previewIndex, setPreviewIndex] = useState(null);
  const lastInventoryRefreshRef = useRef(0);
  const submitPendingRef = useRef(false);
  const chequeInputRefs = useRef([]);
  const INVENTORY_REFRESH_MIN_MS = 0;

  const getBookingFlatOptions = (flat, index) => {
    const selectedFloor = hasFloorValue(flat.floors)
      ? normalizeFloorValue(flat.floors)
      : hasFloorValue(flat.floor)
        ? normalizeFloorValue(flat.floor)
        : "";

    if (!flat.configurations || !hasFloorValue(selectedFloor)) {
      return [];
    }

    const excludedFlatNames = flatBookings
      .map((item, itemIndex) =>
        itemIndex === index ? "" : String(item?.flat_name || "").trim(),
      )
      .filter(Boolean);

    return filterEligibleBookingInventoryFlats(mainInventory, {
      flatType: flat.configurations,
      floor: selectedFloor,
      excludeFlatNames: excludedFlatNames,
    });
  };

  const getBookingFloorOptions = (flat) =>
    getEligibleFloorsForConfiguration(mainInventory, flat.configurations);

  const getPropertyDetailSelector = useSelector((state) => state.propertyData);
  const masterDataSelector = useSelector((state) => state.paymentTypes);
  const { response: res } = useSelector((state) => state.login);

  useEffect(() => {
    if (getPropertyDetailSelector.response) {
      const { response } = getPropertyDetailSelector;
      if (response?.status === 200) {
        const details = handleDetailResponse(response?.data);
        if (
          appointmentData?.property_id &&
          details?._id &&
          String(details._id) !== String(appointmentData.property_id)
        ) {
          return;
        }
        setPropDetails(details);
      } else {
        setPropDetails({});
      }
    }
  }, [getPropertyDetailSelector, appointmentData?.property_id]);

  useEffect(() => {
    if (masterDataSelector.response) {
      const { response } = masterDataSelector;
      if (response?.status === 200) {
        setMasterType(handleListResponse(response.data));
      } else {
        setMasterType([]);
      }
    }
  }, [masterDataSelector]);

  const handleGetInventoryList = async ({
    silent = false,
    force = false,
  } = {}) => {
    if (!propDetails?.jw_project_id) {
      setInventoryLoadState("empty");
      setInventoryHint(strings.noInventoryLoaded);
      return;
    }

    const now = Date.now();
    if (
      !force &&
      now - lastInventoryRefreshRef.current < INVENTORY_REFRESH_MIN_MS
    ) {
      return;
    }
    lastInventoryRefreshRef.current = now;

    if (!silent) {
      setLoading(true);
    }
    setInventoryLoadState("loading");
    setInventoryHint("");

    try {
      const inventoryRes = await apiCall(
        "post",
        apiEndPoints.GETPROPERTYDETAILSJW,
        {
          jw_property_id: propDetails?.jw_project_id,
          suppressGlobalLoader: true,
        },
      );

      const temp = extractInventoryRows(inventoryRes);
      if (temp.length > 0) {
        setMainInventory(temp);
        setFlatTypes(getUniqueFlatTypesFromInventory(temp));
        setInventoryLoadState("loaded");
        setInventoryHint("");
      } else {
        setMainInventory([]);
        setFlatTypes([]);
        setInventoryLoadState("empty");
        setInventoryHint(inventoryErrorMessage(inventoryRes));
      }
    } catch (e) {
      setMainInventory([]);
      setFlatTypes([]);
      setInventoryLoadState("error");
      setInventoryHint(
        e?.response?.data?.message ||
          inventoryErrorMessage(e?.response) ||
          strings.noInventoryLoaded,
      );
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleInventoryDropdownFocus = () => {
    handleGetInventoryList({ silent: true, force: true });
  };

  useEffect(() => {
    if (propDetails?.jw_project_id) {
      handleGetInventoryList({ force: true });
    }
    // Refresh when the JW project id is available; avoid re-fetching on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propDetails?.jw_project_id]);

  const { appointment_id } = params || {};

  const appointmentDetailSelector = useSelector((state) => state.appointment);
  const visitorDetailSelector = useSelector((state) => state.visitor);

  const handleOkClick = () => {
    dispatch(removeAddBookingData());
    setOkBookingIsVisible(false);
    navigate("/booking_list");
  };

  useEffect(() => {
    if (appointment_id) {
      dispatch(getAppointmentDetail({ appointment_id }));
    }
  }, [appointment_id, dispatch]);

  useEffect(() => {
    if (appointment_id) {
      dispatch(getSiteVisitStatusData({ appointment_id }))
        .then((statusRes) => {
          if (statusRes?.data?.length > 0) {
            setSiteVisitMapping(statusRes.data[0]);
          }
        })
        .catch(() => {});
    }
  }, [appointment_id, dispatch]);

  useEffect(() => {
    dispatch(paymentTypes({ type: 10 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(visitorDetail({ lead_id: appointmentData.lead_id }));
  }, [dispatch, appointmentData]);

  useLayoutEffect(() => {
    if (appointmentData?._id && appointmentData?.property_id) {
      dispatch(getPropertyDetail({ property_id: appointmentData.property_id }));
    }
  }, [dispatch, appointmentData?._id, appointmentData?.property_id]);

  useEffect(() => {
    if (visitorDetailSelector.response) {
      const { response } = visitorDetailSelector;
      if (response?.status === 200) {
        setVisitor(response?.data[0]);
      }
    }
  }, [visitorDetailSelector]);

  useEffect(() => {
    if (appointmentDetailSelector.response) {
      const { response } = appointmentDetailSelector;
      if (response?.status === 200) {
        setAppointmentData(handleDetailResponse(response?.data));
      } else {
        setAppointmentData({});
      }
    }
  }, [appointmentDetailSelector]);

  const leadSource =
    Array.isArray(appointmentData.lead_source) &&
    appointmentData.lead_source.length > 0
      ? appointmentData.lead_source[0]
      : null;

  const patchFlat = (index, updater) => {
    setFlatBookings((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? updater(item) : item,
      ),
    );
  };

  const handleFlatFieldChange = (index, e) => {
    const { name, value } = e.target;
    const validAmount = /^[0-9]*\.?[0-9]*$/;

    if (
      name === "amount" ||
      name === "agreement_value" ||
      name === "rate_achieved"
    ) {
      if (validAmount.test(value)) {
        patchFlat(index, (item) => ({ ...item, [name]: value }));
      }
      return;
    }

    if (name === "floors") {
      patchFlat(index, (item) => ({
        ...item,
        floors: value,
        floor: value,
        flat_name: "",
      }));
      return;
    }

    if (name === "configurations") {
      patchFlat(index, (item) => ({
        ...item,
        configurations: value,
        floors: "",
        floor: "",
        flat_name: "",
        saleable_area: "",
        carpet_area: "",
      }));
      return;
    }

    if (name === "flat_name") {
      const selectedFlat = (Array.isArray(mainInventory) ? mainInventory : []).find(
        (flat) => flat["Flat Name"] === value,
      );
      if (!selectedFlat) {
        patchFlat(index, (item) => ({ ...item, flat_name: value }));
        return;
      }
      patchFlat(index, (item) => ({
        ...item,
        flat_name: value,
        carpet_area: selectedFlat["Carpet Area"] ?? "",
        floors: selectedFlat["Floor"],
        floor: selectedFlat["Floor"],
        configurations: selectedFlat["Flat Type"],
        saleable_area: selectedFlat["Saleable Area"] ?? "",
      }));
      return;
    }

    patchFlat(index, (item) => ({ ...item, [name]: value }));
  };

  const handleChequeChange = (index, e) => {
    if (!e.target.files?.length) {
      return;
    }
    const file = e.target.files[0];
    const validFile = ValidateExtensionImge(file);
    if (validFile === true) {
      patchFlat(index, (item) => ({
        ...item,
        chequePreview: URL.createObjectURL(file),
        chequeRaw: file,
      }));
      setFormVaild((prev) => ({ ...prev, [`validMedia_${index}`]: null }));
    } else {
      patchFlat(index, (item) => ({
        ...item,
        chequePreview: "",
        chequeRaw: null,
      }));
      setFormVaild((prev) => ({
        ...prev,
        [`validMedia_${index}`]: validFile,
      }));
    }
  };

  const validateFlat = (flat) => {
    const errors = {};
    if (!flat.amount || isNaN(flat.amount)) {
      errors.amount = "Amount is required and must be a valid number.";
    }
    if (!flat.agreement_value || isNaN(flat.agreement_value)) {
      errors.agreement_value =
        "Agreement value is required and must be a valid number.";
    }
    if (!flat.rate_achieved || isNaN(flat.rate_achieved)) {
      errors.rate_achieved =
        "Rate achieved is required and must be a valid number.";
    }
    if (!flat.payment_type) {
      errors.payment_type = "Please select a payment type.";
    }
    if (!flat.configurations) {
      errors.configurations = "Please select a configuration.";
    }
    if (!hasFloorValue(flat.floors)) {
      errors.floors = "Please select a floor.";
    }
    if (!flat.flat_name) {
      errors.flat_name = "Please select a flat.";
    }
    if (!flat.carpet_area) {
      errors.carpet_area = "Carpet area is required.";
    }
    if (!flat.chequeRaw) {
      errors.cheque_image = "Please attach a cheque / payment photo.";
    }
    if (!String(flat.comment || "").trim()) {
      errors.comment = strings.commentErr || "Comment is required.";
    }
    return errors;
  };

  const validateAllFlats = () => {
    const nextErrors = flatBookings.map((flat) => validateFlat(flat));
    setFormErrors(nextErrors);
    const firstInvalid = nextErrors.findIndex(
      (errors) => Object.keys(errors).length > 0,
    );
    if (firstInvalid >= 0) {
      const firstMessage = Object.values(nextErrors[firstInvalid])[0];
      errorToast(
        flatBookings.length > 1
          ? `Flat ${firstInvalid + 1}: ${firstMessage}`
          : firstMessage,
      );
      return false;
    }
    return true;
  };

  const addMoreFlats = () => {
    if (!validateAllFlats()) {
      return;
    }
    setFlatBookings((prev) => [...prev, createEmptyFlatBooking()]);
    setFormErrors((prev) => [...prev, {}]);
  };

  const deleteFlat = (index) => {
    if (flatBookings.length <= 1) {
      return;
    }
    setFlatBookings((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    setFormErrors((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    if (previewIndex === index) {
      setPreviewIndex(null);
    }
  };

  const buildFlatFormData = (flat) => {
    const formData = new FormData();
    visitor?.customer_detail?._id &&
      formData.append("customer_id", visitor?.customer_detail?._id);
    propDetails?.jw_project_id &&
      formData.append("jw_project_id", propDetails?.jw_project_id);
    appointmentData.crm_person_email &&
      formData.append("crm_person_email", appointmentData.crm_person_email);
    appointmentData.lead_source &&
      formData.append("lead_source", appointmentData.lead_source);
    appointmentData.lead_id &&
      formData.append("lead_id", appointmentData.lead_id);
    appointmentData.property_id &&
      formData.append("property_id", appointmentData.property_id);
    appointmentData._id &&
      formData.append("appointment_id", appointmentData._id);
    if (flat.chequeRaw) {
      formData.append("cheque_image", flat.chequeRaw);
    }
    flat.amount && formData.append("booking_amount", flat.amount);
    flat.amount && formData.append("amount", flat.amount);
    flat.agreement_value &&
      formData.append("agreement_value", flat.agreement_value);
    flat.rate_achieved && formData.append("rate_achieved", flat.rate_achieved);
    flat.payment_type && formData.append("payment_type", flat.payment_type);
    flat.configurations && formData.append("flat_type", flat.configurations);
    if (hasFloorValue(flat.floors)) {
      formData.append("floor", normalizeFloorValue(flat.floors));
    }
    flat.flat_name && formData.append("flat_no", flat.flat_name);
    flat.carpet_area && formData.append("carpet_area", flat.carpet_area);
    flat.saleable_area && formData.append("saleable_area", flat.saleable_area);
    const commentText = String(flat.comment || "").trim();
    if (commentText) {
      formData.append("description", commentText);
    }
    formData.append("booking_status", "2");
    return formData;
  };

  const handleBookingSuccess = (payload) => {
    if (siteVisitMapping?.jv_site_visit_id) {
      dispatch(
        updateSiteVisitStatus({
          jvSiteVisitId: siteVisitMapping.jv_site_visit_id,
          closingStatus: "Booked",
          role: res?.data?.role_title || "auto",
        }),
      ).catch(() => {});
    }
    const jw = payload?.jw;
    if (jw?.attempted && jw?.success) {
      toast.success(jw.message || strings.jwSyncSuccess);
    } else if (jw?.attempted && !jw?.success && !jw?.pending) {
      toast.error(
        jw.message ||
          "Booking saved. If the JW booking number is missing, open Booking Details and tap Sync to JW.",
      );
    }
    navigate("/dashboard");
  };

  const handleSubmit = async () => {
    if (loading || submitPendingRef.current) {
      return;
    }
    if (!validateAllFlats()) {
      return;
    }
    submitPendingRef.current = true;
    setLoading(true);
    let lastPayload = null;
    let savedSuccessfully = false;
    try {
      for (let index = 0; index < flatBookings.length; index += 1) {
        const bookingRes = await bookingService.addBooking(
          buildFlatFormData(flatBookings[index]),
        );
        if (bookingRes?.data?.status !== 200) {
          errorToast(
            bookingRes?.data?.message ||
              `Could not save flat ${index + 1}. Please try again.`,
          );
          return;
        }
        lastPayload = bookingRes.data;
        showActionSuccessWithMdoc(bookingRes.data.message, bookingRes.data.mdoc);
      }
      if (lastPayload) {
        dispatch({ type: ADD_BOOKING, payload: lastPayload });
        handleBookingSuccess(lastPayload);
        savedSuccessfully = true;
      }
    } catch (e) {
      const { response = strings.initialError } = e;
      unAuthorizedError(response?.data || response);
    } finally {
      if (!savedSuccessfully) {
        submitPendingRef.current = false;
        setLoading(false);
      }
    }
  };

  const visitorName =
    `${appointmentData.customer_first_name || ""} ${appointmentData.customer_last_name || ""}`.trim();
  const propertyTitle =
    propDetails?.property_title ||
    appointmentData?.property_title ||
    appointmentData?.property_name ||
    "";
  const previewFlat =
    previewIndex !== null ? flatBookings[previewIndex] : null;

  return (
    <div className="page-body justo-booking-page">
      <Breadcrumb
        title={strings.addBooking}
        parentListing={strings.bookingList}
        url="/booking_list"
        titleactive={strings.addBooking}
        isBackBtn={true}
      />
      <div className="container-fluid justo-booking">
        <div className="justo-booking__card justo-booking-form">
          <div className="justo-booking__top justo-booking-form__top">
            <div className="justo-booking__title-block">
              <h1 className="justo-booking__title">{strings.addBooking}</h1>
              <p className="justo-booking__subtitle">
                {propertyTitle
                  ? `${visitorName || "Visitor"} · ${propertyTitle}`
                  : "Payment, inventory, and cheque details"}
              </p>
            </div>
            <div className="justo-booking-form__top-actions">
              {!isEmpty(formVaild) && (
                <span className="justo-booking__error">
                  {strings.fieldRequired}
                </span>
              )}
              <button
                className="justo-booking__btn justo-booking__btn--ghost"
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                {strings.cancel}
              </button>
              <button
                className="justo-booking__btn justo-booking__btn--solid"
                type="button"
                onClick={() => handleSubmit()}
                disabled={loading}
              >
                {loading ? "Submitting…" : strings.submit}
              </button>
            </div>
          </div>

          <div className="justo-booking-form__meta" aria-label="Visitor details">
            <div className="justo-booking-form__fact">
              <span className="justo-booking-form__fact-label">{strings.name}</span>
              <span className="justo-booking-form__fact-value">
                {visitorName || "—"}
              </span>
            </div>
            <div className="justo-booking-form__fact">
              <span className="justo-booking-form__fact-label">
                {strings.crmPersonEmail}
              </span>
              <span className="justo-booking-form__fact-value">
                {appointmentData.crm_person_email || "—"}
              </span>
            </div>
            <div className="justo-booking-form__fact">
              <span className="justo-booking-form__fact-label">
                {strings.leadSource}
              </span>
              <span className="justo-booking-form__fact-value">
                {appointmentData.lead_source || "—"}
              </span>
            </div>
            {leadSource === "Channel Partner" && (
              <>
                <div className="justo-booking-form__fact">
                  <span className="justo-booking-form__fact-label">
                    {strings.cpName}
                  </span>
                  <span className="justo-booking-form__fact-value">
                    {appointmentData.cp_name || "—"}
                  </span>
                </div>
                <div className="justo-booking-form__fact">
                  <span className="justo-booking-form__fact-label">
                    {strings.cpEmployeeName}
                  </span>
                  <span className="justo-booking-form__fact-value">
                    {appointmentData.cp_emp_name || "NA"}
                  </span>
                </div>
              </>
            )}
            {(leadSource === "Reference" ||
              leadSource === "Referral Partner") && (
              <>
                <div className="justo-booking-form__fact">
                  <span className="justo-booking-form__fact-label">
                    {strings.referrerNumber}
                  </span>
                  <span className="justo-booking-form__fact-value">
                    {appointmentData.referrer_contact || "—"}
                  </span>
                </div>
                <div className="justo-booking-form__fact">
                  <span className="justo-booking-form__fact-label">
                    {strings.referrerName}
                  </span>
                  <span className="justo-booking-form__fact-value">
                    {appointmentData.referrer_name || "NA"}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="justo-booking-form__body">
            {flatBookings.map((flat, index) => {
              const errors = formErrors[index] || {};
              const isLast = index === flatBookings.length - 1;
              return (
                <div className="justo-booking-form__unit" key={`flat-${index}`}>
                  <div className="justo-booking-form__unit-bar">
                    <h2 className="justo-booking-form__unit-title">
                      {strings.flatUnitTitle} {index + 1}
                    </h2>
                    {isLast ? (
                      <div className="justo-booking-form__unit-actions">
                        <button
                          type="button"
                          className="justo-booking__btn justo-booking__btn--solid justo-booking__btn--sm"
                          onClick={addMoreFlats}
                          disabled={loading}
                        >
                          {strings.addMoreFlats}
                        </button>
                        {flatBookings.length > 1 ? (
                          <button
                            type="button"
                            className="justo-booking__btn justo-booking__btn--ghost justo-booking__btn--sm"
                            onClick={() => deleteFlat(index)}
                            disabled={loading}
                          >
                            {strings.deleteFlat}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <section className="justo-booking-form__panel">
                    <h2 className="justo-booking-form__section-title">Payment</h2>
                    <div className="justo-booking-form__grid justo-booking-form__grid--2">
                      <label
                        className="justo-booking__field"
                        htmlFor={`amount-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.amount}
                        </span>
                        <input
                          type="number"
                          className="form-control justo-booking__input"
                          id={`amount-${index}`}
                          name="amount"
                          value={flat.amount || ""}
                          onChange={(e) => handleFlatFieldChange(index, e)}
                        />
                        {errors.amount ? (
                          <span className="justo-booking__error">
                            {errors.amount}
                          </span>
                        ) : null}
                      </label>
                      <label
                        className="justo-booking__field"
                        htmlFor={`payment_type-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.PaymentType}
                        </span>
                        <select
                          className="form-control justo-booking__input"
                          id={`payment_type-${index}`}
                          name="payment_type"
                          value={flat.payment_type || ""}
                          onChange={(e) => handleFlatFieldChange(index, e)}
                        >
                          <option value="" disabled>
                            Select payment type
                          </option>
                          {masterType.map((item) => (
                            <option key={item._id} value={item.title}>
                              {item.title}
                            </option>
                          ))}
                        </select>
                        {errors.payment_type ? (
                          <span className="justo-booking__error">
                            {errors.payment_type}
                          </span>
                        ) : null}
                      </label>
                      <label
                        className="justo-booking__field"
                        htmlFor={`agreement_value-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.agreementValue}
                        </span>
                        <input
                          type="number"
                          className="form-control justo-booking__input"
                          id={`agreement_value-${index}`}
                          name="agreement_value"
                          value={flat.agreement_value || ""}
                          onChange={(e) => handleFlatFieldChange(index, e)}
                        />
                        {errors.agreement_value ? (
                          <span className="justo-booking__error">
                            {errors.agreement_value}
                          </span>
                        ) : null}
                      </label>
                      <label
                        className="justo-booking__field"
                        htmlFor={`rate_achieved-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.rateAchieved}
                        </span>
                        <input
                          type="number"
                          className="form-control justo-booking__input"
                          id={`rate_achieved-${index}`}
                          name="rate_achieved"
                          value={flat.rate_achieved || ""}
                          onChange={(e) => handleFlatFieldChange(index, e)}
                        />
                        {errors.rate_achieved ? (
                          <span className="justo-booking__error">
                            {errors.rate_achieved}
                          </span>
                        ) : null}
                      </label>
                    </div>

                    <div className="justo-booking-form__upload">
                      <span className="justo-booking__label">
                        {strings.attachPhoto}
                      </span>
                      <div
                        className={`justo-booking-form__upload-row${
                          flat.chequePreview ? " has-preview" : ""
                        }`}
                      >
                        {flat.chequePreview ? (
                          <button
                            type="button"
                            className="justo-booking-form__preview-btn"
                            onClick={() => setPreviewIndex(index)}
                            title={strings.viewPhoto}
                          >
                            <img
                              src={flat.chequePreview}
                              alt="Uploaded cheque preview"
                              className="justo-booking-form__preview-img"
                            />
                          </button>
                        ) : (
                          <div className="justo-booking-form__preview-empty">
                            <i className="fa fa-image" aria-hidden="true" />
                          </div>
                        )}
                        <div className="justo-booking-form__upload-meta">
                          <span className="justo-booking-form__upload-name">
                            {flat.chequePreview
                              ? flat.chequeRaw?.name || strings.viewPhoto
                              : strings.noPhotoSelected}
                          </span>
                          <div className="justo-booking-form__upload-actions">
                            <button
                              type="button"
                              className="justo-booking__btn justo-booking__btn--solid justo-booking__btn--sm"
                              onClick={() =>
                                chequeInputRefs.current[index]?.click()
                              }
                            >
                              {strings.browse}
                            </button>
                            {flat.chequePreview ? (
                              <button
                                type="button"
                                className="justo-booking__btn justo-booking__btn--ghost justo-booking__btn--sm"
                                onClick={() => {
                                  patchFlat(index, (item) => ({
                                    ...item,
                                    chequePreview: "",
                                    chequeRaw: null,
                                  }));
                                  if (previewIndex === index) {
                                    setPreviewIndex(null);
                                  }
                                }}
                              >
                                {strings.removePhoto}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <input
                        type="file"
                        id={`upload-button-${index}`}
                        accept="image/*"
                        style={{ display: "none" }}
                        ref={(el) => {
                          chequeInputRefs.current[index] = el;
                        }}
                        onChange={(e) => handleChequeChange(index, e)}
                      />
                      {errors.cheque_image ? (
                        <span className="justo-booking__error">
                          {errors.cheque_image}
                        </span>
                      ) : null}
                      {formVaild?.[`validMedia_${index}`] ? (
                        <span className="justo-booking__error">
                          {formVaild[`validMedia_${index}`]}
                        </span>
                      ) : null}
                    </div>
                  </section>

                  <section className="justo-booking-form__panel">
                    <h2 className="justo-booking-form__section-title">
                      Inventory
                    </h2>
                    {index === 0 &&
                    (inventoryLoadState === "empty" ||
                      inventoryLoadState === "error") ? (
                      <p className="justo-booking-form__inventory-hint">
                        {inventoryHint || strings.noInventoryLoaded}
                      </p>
                    ) : null}
                    <div className="justo-booking-form__grid justo-booking-form__grid--2">
                      <label
                        className="justo-booking__field"
                        htmlFor={`configurations-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.configuration}
                        </span>
                        <select
                          className="form-control justo-booking__input"
                          id={`configurations-${index}`}
                          name="configurations"
                          value={flat.configurations || ""}
                          onChange={(e) => handleFlatFieldChange(index, e)}
                          disabled={
                            !Array.isArray(flatTypes) || flatTypes.length === 0
                          }
                        >
                          <option value="" disabled>
                            Select configuration
                          </option>
                          {(Array.isArray(flatTypes) ? flatTypes : []).map(
                            (flatType, typeIndex) => (
                              <option
                                key={`${flatType}-${typeIndex}`}
                                value={flatType}
                              >
                                {flatType}
                              </option>
                            ),
                          )}
                        </select>
                        {errors.configurations ? (
                          <span className="justo-booking__error">
                            {errors.configurations}
                          </span>
                        ) : null}
                      </label>
                      <label
                        className="justo-booking__field"
                        htmlFor={`floors-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.floors}
                        </span>
                        <select
                          className="form-control justo-booking__input"
                          id={`floors-${index}`}
                          name="floors"
                          value={
                            hasFloorValue(flat.floors)
                              ? normalizeFloorValue(flat.floors)
                              : ""
                          }
                          onChange={(e) => handleFlatFieldChange(index, e)}
                          disabled={!flat.configurations}
                        >
                          <option value="" disabled>
                            {strings.selectFloor}
                          </option>
                          {getBookingFloorOptions(flat).map(
                            (floor, floorIndex) => (
                              <option
                                key={`${floor}-${floorIndex}`}
                                value={floor}
                              >
                                {floor}
                              </option>
                            ),
                          )}
                        </select>
                        {errors.floors ? (
                          <span className="justo-booking__error">
                            {errors.floors}
                          </span>
                        ) : null}
                      </label>
                      <label
                        className="justo-booking__field"
                        htmlFor={`flat_name-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.flatName}
                        </span>
                        <select
                          className="form-control justo-booking__input"
                          id={`flat_name-${index}`}
                          name="flat_name"
                          value={flat.flat_name || ""}
                          onChange={(e) => handleFlatFieldChange(index, e)}
                          onFocus={handleInventoryDropdownFocus}
                          disabled={!hasFloorValue(flat.floors)}
                        >
                          <option value="" disabled>
                            {strings.selectFlat}
                          </option>
                          {getBookingFlatOptions(flat, index).map(
                            (item, itemIndex) => {
                              const display = getInventoryFlatDisplayParts(item);
                              const flatNameValue = item["Flat Name"];
                              return (
                                <option
                                  key={`${flatNameValue}-${itemIndex}`}
                                  value={flatNameValue}
                                >
                                  {display.detailLabel || display.optionLabel}
                                </option>
                              );
                            },
                          )}
                        </select>
                        {errors.flat_name ? (
                          <span className="justo-booking__error">
                            {errors.flat_name}
                          </span>
                        ) : null}
                      </label>
                      <label
                        className="justo-booking__field"
                        htmlFor={`carpet_area-${index}`}
                      >
                        <span className="justo-booking__label">
                          {strings.carpetArea}
                        </span>
                        <input
                          type="text"
                          className="form-control justo-booking__input"
                          id={`carpet_area-${index}`}
                          name="carpet_area"
                          value={
                            flat.carpet_area
                              ? `${flat.carpet_area} Sq. ft.`
                              : ""
                          }
                          readOnly
                        />
                        {errors.carpet_area ? (
                          <span className="justo-booking__error">
                            {errors.carpet_area}
                          </span>
                        ) : null}
                      </label>
                    </div>

                    <label
                      className="justo-booking__field justo-booking-form__comment"
                      htmlFor={`comment-${index}`}
                    >
                      <span className="justo-booking__label">
                        {strings.comment}
                        <span className="required-field" />
                      </span>
                      <textarea
                        className="form-control justo-booking__input justo-booking-form__textarea"
                        id={`comment-${index}`}
                        name="comment"
                        value={flat.comment || ""}
                        onChange={(e) => handleFlatFieldChange(index, e)}
                        rows="3"
                      />
                      {errors.comment ? (
                        <span className="justo-booking__error">
                          {errors.comment}
                        </span>
                      ) : null}
                    </label>
                  </section>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {previewFlat?.chequePreview ? (
        <div
          className="justo-booking-form__lightbox"
          onClick={() => setPreviewIndex(null)}
          role="presentation"
        >
          <div
            className="justo-booking-form__lightbox-inner"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <img
              src={previewFlat.chequePreview}
              alt="Cheque full preview"
              className="justo-booking-form__lightbox-img"
            />
            <button
              type="button"
              className="justo-booking__btn justo-booking__btn--solid justo-booking__btn--sm"
              onClick={() => setPreviewIndex(null)}
            >
              {strings.cancel}
            </button>
          </div>
        </div>
      ) : null}

      {okBookingIsVisible && (
        <div className="justo-booking-form__lightbox" role="presentation">
          <div
            className="justo-booking-form__success"
            role="dialog"
            aria-modal="true"
          >
            <h2 className="justo-booking-form__success-title">Success</h2>
            <p className="justo-booking-form__success-copy">
              Booking has been sent to CRM.
            </p>
            <button
              type="button"
              className="justo-booking__btn justo-booking__btn--solid"
              onClick={handleOkClick}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default AddBooking;
